/* eslint-disable arrow-body-style */
import * as React from "react";
import {Component} from "react";

import {observer} from "mobx-react";
import {AppState} from "../../components/app/app-mobx";

import {Button, Divider, Header, Item, Message, Popup, Table} from "semantic-ui-react";
import {MapViewerConfiguration} from "../../config/map-viewer-configuration";
import {NavigationConfiguration} from "../../config/navigation-configuration";
import {AppConfiguration} from "../../core/config/app-configuration";

import {MapState} from "../../core/map-state";
import {Auftragsverwaltung} from "../../stores/auftragsverwaltung-mobx";
import {Project} from "../../models/project";
import {LayerProjectFilter} from "../../components/app/map/layer-project-filter/layer-project-filter";
import {Icon} from "../../core/components/icon/icon";
import {ProjectForm} from "./project-form";

import "./project-details.scss";
import {ProcessSourceList} from "../process-sources/process-source-list";
import {ContractorCard} from "../contractors/contractor-card";
import {PartialOrderGhkList} from "../partial-orders/partial-order-ghk-list";
import {PartialOrderGkList} from "../partial-orders/partial-order-gk-list";
import {PartialOrderRiskList} from "../partial-orders/partial-order-risk-list";
import {ProjectMunicipality} from "../../models/project-municipality";
import {Municipality} from "../../models/municipality";
import {Contractor} from "../../models/contractor";
import {EditorTrackingMetadata} from "../../components/app/metadata/editor-tracking-metadata";
import {ProjectPerimeterInfo} from "../project-perimeters/project-perimeter-info";
import {DocumentList} from "../documents/document-list";

export interface ProjectDetailsProps {
    view: __esri.MapView | __esri.SceneView | undefined;
    auftragsverwaltung: Auftragsverwaltung;
    project: Project | null;
    appStore: AppState;
    mapStore: MapState;
    mapViewerConfig: MapViewerConfiguration;
    appConfig: AppConfiguration;
    prepareContact?: (project: Project) => void;
    handleErrorAndNotFound: () => void;
}

@observer
export class ProjectDetails extends Component<ProjectDetailsProps, any> {

    constructor(props: ProjectDetailsProps) {
        super(props);
        this.state = {
            highlightedItemId: null,
            graphic: null,
            typeCodes: null,
            partialOrdersGk: [],
            partialOrdersGh: [],
            processSources: []
        };
        this.handleToggleEditing = this.handleToggleEditing.bind(this);
        this.handleSave = this.handleSave.bind(this);
        this.handleDelete = this.handleDelete.bind(this);
        this.showContractor = this.showContractor.bind(this);
    }

    componentDidMount(): void {
        this.loadDomains();
        // reload associations to be sure these are loaded when showing details without showing the project list first
        this.reloadProjectMunicipalities();
        this.reloadProjectContractor();
    }

    componentDidUpdate(): void {
        const {appStore, project} = this.props;

        if (!project || !project.id) {
            return;
        }

        const {topic} = appStore;

        let newRoute = "";
        if (project.isNewRecord) {
            newRoute = `${topic.routes[0]}/neu`;
        } else {
            newRoute = `${topic.routes[0]}/${project.id}`;
        }
        const breadcrumbs = new NavigationConfiguration();
        breadcrumbs.items = [
            { href: topic.routes[0], title: topic.label },
            { href: newRoute, title: project.isNewRecord || !project.name ? "Neues Projekt" : project.name }
        ];
        appStore.setBreadcrumbs(breadcrumbs);
        appStore.setRoutePath(newRoute);

        const pageTitle = project.isNewRecord || !project.name ? `${topic.label} - Neues Projekt` : project.name;
        appStore.setPageTitle(pageTitle);
        appStore.setTopContent(null);
        appStore.setBottomContent(null);
    }

    componentWillUnmount(): void {
        /*
         * Avoid `Warning: Can't perform a React state update on an unmounted component. This is a no-op, but it
         * indicates a memory leak in your application. To fix, cancel all subscriptions and asynchronous tasks
         * in the componentWillUnmount method.`
         */
        this.setState({});
    }

    render(): React.ReactElement | null {
        const {appStore, mapStore, mapViewerConfig, view, auftragsverwaltung} = this.props;
        const {typeCodes} = this.state;
        if (!view) {
            return null;
        }
        const project = auftragsverwaltung.selectedProject;
        if (!project || !project.id) {
            return null;
        }

        let element: React.ReactElement | null;
        if (auftragsverwaltung.isEditingProject) {
            element = (
                <ProjectForm
                    view={view}
                    appStore={appStore}
                    auftragsverwaltung={auftragsverwaltung}
                    project={project}
                    onCancel={(e: React.MouseEvent) => {
                        this.handleToggleEditing(e);
                    }}
                    onSave={() => {
                        this.handleSave();
                    }}
                />
            );
        }
        else {
            element = (
                <Item.Content>
                    <Table basic="very" compact>
                        <Table.Body>
                            <Table.Row>
                                <Table.Cell collapsing singleLine>Projekttyp</Table.Cell>
                                <Table.Cell>{typeCodes ? typeCodes.get(project.typeId) : project.typeId}</Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell collapsing singleLine>Projektnummer</Table.Cell>
                                <Table.Cell>{project.id}</Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell collapsing singleLine>Projektnummer vif</Table.Cell>
                                <Table.Cell>{project.uuid || "-"}</Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell singleLine>Projektname</Table.Cell>
                                <Table.Cell>{project.name}</Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell singleLine>Projektgrund</Table.Cell>
                                <Table.Cell>{project.reason}</Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell singleLine>Gemeinden</Table.Cell>
                                <Table.Cell className="multi line">
                                    {project.municipalities?.map((m: Municipality) => m.name).join(" | ")}
                                </Table.Cell>
                            </Table.Row>
                            {project.isTypeRisk &&
                                <Table.Row>
                                    <Table.Cell singleLine>GFK Oberflächenabfluss</Table.Cell>
                                    <Table.Cell singleLine>
                                        <Icon
                                            className={`geo-icon-lg ${project.gkoa ? "geo-icon-ok" : undefined}`}
                                            name={project.gkoa ? "ok" : "error"} />
                                    </Table.Cell>
                                </Table.Row>
                            }
                            {project.isTypeRisk &&
                                <Table.Row>
                                    <Table.Cell singleLine>GHK Wasser</Table.Cell>
                                    <Table.Cell singleLine>
                                        <Icon
                                            className={`geo-icon-lg ${project.ghkw ? "geo-icon-ok" : undefined}`}
                                            name={project.ghkw ? "ok" : "error"} />
                                    </Table.Cell>
                                </Table.Row>
                            }
                            <Table.Row>
                                <Table.Cell singleLine>Perimeter</Table.Cell>
                                <Table.Cell singleLine>
                                    <ProjectPerimeterInfo auftragsverwaltung={auftragsverwaltung} project={project} />
                                </Table.Cell>
                            </Table.Row>
                        </Table.Body>
                    </Table>
                </Item.Content>
            );
        }

        return (<div className="project-details">
            <LayerProjectFilter
                appStore={appStore}
                auftragsverwaltung={auftragsverwaltung}
                mapStore={mapStore}
                mapViewerConfig={mapViewerConfig}
            />
            {!auftragsverwaltung.isEditingProject && project.isAlterable &&
                <Popup content="Projekt ändern"
                    trigger={
                        <Button className={"editing"} circular
                            onClick={(e: React.MouseEvent) => {
                                this.handleToggleEditing(e);
                            }}>
                            <Icon name={"pencil"} />
                        </Button>
                    } />
            }
            {!auftragsverwaltung.isEditingProject && !project.isNewRecord && project.isAlterable &&
                <Popup content="Projekt löschen"
                    trigger={
                        <Button className={"delete"} circular
                            data={{}}
                            onClick={(e: React.MouseEvent) => {
                                this.handleDelete(e, project);
                            }}>
                            <Icon name={"garbage_can"} />
                        </Button>
                    } />
            }
            {element}
            {!project.isNewRecord &&
                <>
                    <Divider horizontal>
                        <Header as="h2">
                            <Icon name="office_building2" />
                            Auftragnehmer
                        </Header>
                    </Divider>
                    <Item.Group divided>
                        <ContractorCard
                            auftragsverwaltung={auftragsverwaltung}
                            contractor={project.contractor}
                            handleShowContractor={(c: Contractor) => this.showContractor(c) }
                        />
                    </Item.Group>
                </>
            }
            {!project.isNewRecord &&
                project.hasPartialOrdersGk &&
                <>
                    <Divider horizontal>
                        <Header as="h2">
                            <Icon name="pieces" />
                            <Icon name="sign_warning" />
                            Teilaufträge Gefahrenkarte
                        </Header>
                    </Divider>
                    <PartialOrderGkList
                        appStore={appStore}
                        auftragsverwaltung={auftragsverwaltung}
                        project={project} />
                </>
            }
            {!project.isNewRecord &&
                project.hasPartialOrdersGhk &&
                <>
                    <Divider horizontal>
                        <Header as="h2">
                            <Icon name="pieces" />
                            <Icon name="hand_point_up" />
                            Teilaufträge Gefahrenhinweiskarte
                        </Header>
                    </Divider>
                    <PartialOrderGhkList
                        appStore={appStore}
                        auftragsverwaltung={auftragsverwaltung}
                        project={project} />
                </>
            }
            {!project.isNewRecord &&
                project.hasPartialOrdersRisk &&
                <>
                    <Divider horizontal>
                        <Header as="h2">
                            <Icon name="pieces" />
                            Teilaufträge Risikoübersicht
                        </Header>
                    </Divider>
                    <PartialOrderRiskList
                        appStore={appStore}
                        auftragsverwaltung={auftragsverwaltung}
                        project={project} />
                </>
            }
            {!project.isNewRecord &&
                <>
                    <Divider horizontal>
                        <Header as="h2">
                            <Icon name="arrow_from" />
                            Prozessquellen
                        </Header>
                    </Divider>
                    <Item.Group className={"geo-process-sources"}>
                        <ProcessSourceList
                            auftragsverwaltung={auftragsverwaltung}
                            project={project}
                            key={"process-sources-" + project.id} />
                    </Item.Group>
                </>
            }
            {!project.isNewRecord && project.typeId !== 4 &&
                <>
                    <Divider horizontal>
                        <Header as="h2">
                            <Icon name="arrow_from" />
                            Technische Dokumentation
                        </Header>
                    </Divider>
                    <Item.Group className={"geo-documents"}>
                        <Message className="info" size="tiny">
                            Technische Berichte und Faktenblätter sind im Datenportal Naturgefahren
                            in den entsprechenden Ordner abzulegen.
                            &nbsp;<a className="link" href="https://www.geo.lu.ch/datenportal/#%2FHOME%2FFaktenblaetter" rel="noreferrer" target="_blank">Faktenblätter</a>&nbsp;|
                            &nbsp;<a className="link" href="https://www.geo.lu.ch/datenportal/#%2FHOME%2FTechnische_Dokumentation" rel="noreferrer"  target="_blank">Technische Dokumente</a>&nbsp;
                        </Message>
                        <DocumentList
                            appStore={appStore}
                            auftragsverwaltung={auftragsverwaltung}
                            project={project}/>
                    </Item.Group>
                </>
            }
            <EditorTrackingMetadata editableItem={project}/>
        </div>);
    }

    private handleToggleEditing(e: React.MouseEvent): void {
        e.preventDefault();
        const target = e.target as HTMLElement;
        target.blur();

        const {auftragsverwaltung} = this.props;
        const {isEditing, editingObjectType} = auftragsverwaltung;

        if (isEditing) {
            if (editingObjectType === "project") {
                // do only turn of editing for this object type
                auftragsverwaltung.setEditing(false);
                auftragsverwaltung.resetStatus();

                const project = auftragsverwaltung.selectedProject;
                if (project?.isNewRecord) {
                    this.cancelNewProject(project);
                }
            }
        } else {
            // do not show map when editing
            auftragsverwaltung.setShowMap(false);
            auftragsverwaltung.setEditing(true, "project");
        }
    }

    private handleSave(): void {
        this.reloadProjectMunicipalities();
        this.reloadProjectContractor();
    }

    private handleDelete(e: React.MouseEvent, project: Project): void {
        e.preventDefault();
        const target = e.target as HTMLElement;
        target.blur();

        if (confirm("Projekt wirklich löschen?")) {
            const {appStore, auftragsverwaltung} = this.props;

            const user = appStore.esriCredential?.userId || "";
            auftragsverwaltung.ProjectStore?.delete(project, user).then(() => {
                this.goBackToList();
            });
        }
    }

    private cancelNewProject(project: Project): void {
        if (!project) {
            return;
        }

        const {auftragsverwaltung} = this.props;

        auftragsverwaltung.ProjectStore?.removeItem(project);
        auftragsverwaltung.ProjectStore.resetSelectedItem();
        this.goBackToList();
    }

    private goBackToList(): void {
        const {appStore} = this.props;

        appStore.clearObjectId();
        // go back to base route (project list)
        appStore.setRoutePath(appStore.topic?.routes[0]);
    }

    private showContractor(contractor: Contractor): void {
        const {auftragsverwaltung, appStore, mapViewerConfig} = this.props;

        if (!contractor) {
            return;
        }

        appStore.setRoutePath("/auftragnehmer");
        const topic = mapViewerConfig.getTopic("contractors");
        if (!topic) {
            throw new Error("Topic not found loading contractors.");
        }
        appStore.setTopic(topic);

        setTimeout(() => {
            // Do this after setting topic. setTopic will unselect contractor otherwise
            appStore.setObjectId(contractor.id.toString());
            auftragsverwaltung.ContractorStore.setSelectedItem(contractor);
        }, 200);
    }

    /**
     * reload related municipalities after updates
     * @private
     */
    private reloadProjectMunicipalities(): void {
        const {project, auftragsverwaltung} = this.props;

        if (!project?.id) {
            return;
        }

        // first get municipalities with full information
        const municipalities = [...auftragsverwaltung.MunicipalityStore.items.values()];
        // municipalities are multipart features, so we expect to get multiple entries per municipality
        // --> make unique for select options
        const unique =  [...new Map(municipalities.map((m) => [m.name, m])).values()];
        const sorted = unique.sort(Municipality.sortByName);

        // load relation and store municipalities related to project
        auftragsverwaltung.ProjectMunicipalityStore.loadItems([project.id])
            .then((projectMunicipalities: ProjectMunicipality[]) => {
                project.municipalities = sorted.filter((m: Municipality) => {
                    return projectMunicipalities.some((pm) => pm.bfsNr === m.bfsNr);
                });
            }).catch((error) => {
                this.setState({
                    error
                });
            });
    }

    private reloadProjectContractor(): void {
        const {project, auftragsverwaltung} = this.props;

        if (!project || !project.contractorId) {
            return;
        }
        auftragsverwaltung.ContractorStore.loadItems([project.contractorId]).then((contractors) => {
            project.contractor = contractors[0];
        });
    }


    /**
     * Load domains
     *
     * @private
     */
    private loadDomains() {
        const {auftragsverwaltung} = this.props;

        auftragsverwaltung.getShortDomainCodes("project", "typeId").then((typeCodes) => {
            this.setState({
                typeCodes
            });
        });
    }

}
